import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Check } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";

export default function ShopDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [id]);

  const buy = async () => {
    if (!user) { login(); return; }
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: p.lookup_key, origin_url: window.location.origin,
        item_type: "product", item_id: p.id,
      });
      window.location.href = r.data.checkout_url;
    } catch { toast.error("Errore checkout"); setBusy(false); }
  };

  if (p === undefined) return <div className="max-w-4xl mx-auto px-4 py-24"><div className="h-96 bg-[#FFFFFF] animate-pulse rounded-2xl" /></div>;
  if (p === null) return <div className="pt-10"><EmptyState title={t("not_found")} desc={t("empty_desc")} /></div>;

  return (
    <div data-testid="shop-detail" className="max-w-5xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-24">
      <button onClick={() => navigate("/shop")} className="inline-flex items-center gap-2 text-sm text-[#9C8F94] hover:text-[#C6899E] mb-10" data-testid="shop-back">
        <ArrowLeft className="w-4 h-4" /> {t("shop_title")}
      </button>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div className="aspect-[3/4] rounded-2xl bg-[#F3E8E2] overflow-hidden border border-[rgba(61,50,56,0.08)]">
          {p.cover_path ? <img src={mediaUrl(p.cover_path)} alt={p.title} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center font-serif text-6xl text-[rgba(198,137,158,0.2)]">{p.title.charAt(0)}</div>}
        </div>

        <div>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-tight">{p.title}</h1>
          <div className="mt-4 font-serif text-3xl text-[#C6899E]">{p.price > 0 ? `€${p.price}` : t("price_free")}</div>
          {p.description && <p className="mt-6 prose-unfiltered">{p.description}</p>}

          <div className="mt-9">
            {p.owned ? (
              <a href={p.download_path ? mediaUrl(p.download_path) : "#"} target="_blank" rel="noreferrer"
                data-testid="download-product"
                className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-7 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C] transition-colors">
                <Download className="w-4 h-4" /> {t("read") /* download */}
              </a>
            ) : p.price > 0 ? (
              <button onClick={buy} disabled={busy} data-testid="buy-product"
                className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-8 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C] transition-colors disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t("buy")}
              </button>
            ) : (
              <span className="text-[#9C8F94]">{t("price_free")}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
